"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useLabelCategoryDescription = exports.EditCategoryPopover = exports.NewCategoryPopover = exports.useCategoryDropdown = exports.newCategoryTestId = void 0;
const tslib_1 = require("tslib");
const catalog_core_1 = require("@tableau/catalog-core");
const catalog_help_url_1 = require("@tableau/catalog-help-url");
const catalog_messages_1 = require("@tableau/catalog-messages");
const NotificationInfoOutlineIcon_1 = require("@tableau/icons/NotificationInfoOutlineIcon");
const PencilBaseIcon_1 = require("@tableau/icons/PencilBaseIcon");
const widgets_1 = require("@tableau/widgets");
const react_query_1 = require("@tanstack/react-query");
const react_1 = tslib_1.__importDefault(require("react"));
const MarkupStyles_1 = require("../style/MarkupStyles");
const useCustomNameValidation_1 = require("./hook/useCustomNameValidation");
const LabelHelpers_1 = require("./LabelHelpers");
const labelCategories_1 = require("./restApi/labelCategories");
const LabelCategories_1 = require("./shared/LabelCategories");
exports.newCategoryTestId = 'New-Category';
const characterLimit = 128;
const queryKey = [exports.newCategoryTestId];
function useCategoryDropdown(disabled, existingCategory) {
    const [category, setCategory] = react_1.default.useState(existingCategory !== null && existingCategory !== void 0 ? existingCategory : '');
    const queryFn = (0, catalog_core_1.useRestFnNoParams)(labelCategories_1.getLabelCategories);
    const { isLoading, data } = (0, react_query_1.useQuery)([exports.newCategoryTestId], { queryFn });
    const element = (react_1.default.createElement("div", null,
        react_1.default.createElement(widgets_1.SingleSelectDropdown, { label: catalog_messages_1.Messages.LabelCategory(), onChange: setCategory, value: category, disabled: disabled, list: react_1.default.createElement(widgets_1.LocallyFilteringList, { items: isLoading
                    ? [{
                            value: 'spinner',
                            content: react_1.default.createElement(widgets_1.ActivitySpinnerWidget, { spinnerSize: widgets_1.ActivitySpinnerSize.Small, testId: exports.newCategoryTestId }),
                        }]
                    : mapLabelCategoriesAsListItems(data), style: { maxHeight: 300 } }) }),
        category
            ? (react_1.default.createElement(react_1.default.Fragment, null,
                react_1.default.createElement("div", { style: { paddingTop: 16 } },
                    react_1.default.createElement(CategoryDescription, { category: category, setCategory: setCategory })),
                react_1.default.createElement("div", { style: { paddingTop: 4 } },
                    react_1.default.createElement(CategoryHelpInformation, { category: category }))))
            : react_1.default.createElement(react_1.default.Fragment, null)));
    const elementOrDisabled = disabled
        ? (react_1.default.createElement(widgets_1.Tooltip, { tooltipContent: react_1.default.createElement("span", null, catalog_messages_1.Messages.LabelCategoryBuiltIn()), position: widgets_1.TooltipPosition.BottomRightCorner, followCursor: true }, element))
        : element;
    return [category, elementOrDisabled, () => setCategory('')];
}
exports.useCategoryDropdown = useCategoryDropdown;
function NewCategoryPopover() {
    const queryClient = (0, react_query_1.useQueryClient)();
    const popoverInstance = react_1.default.useRef(null);
    const customTextValidation = (0, useCustomNameValidation_1.useCustomNameValidation)(queryKey, catalog_messages_1.Messages.LabelCategoryAlreadyExists(), labelCategories_1.getLabelCategories);
    const mutationFn = (0, catalog_core_1.useRestFn)(labelCategories_1.createLabelCategory);
    const submit = (0, react_query_1.useMutation)({
        mutationFn,
        onSuccess: () => {
            widgets_1.ToastWidget.show({
                message: catalog_messages_1.Messages.LabelCategoryCreated({ Category: (0, LabelHelpers_1.localizeLabelCategory)(name) }),
                toastType: widgets_1.ToastType.Success,
                testId: 'new-category-success',
            });
            onClose();
        },
        onError: () => {
            widgets_1.ToastWidget.show({
                message: catalog_messages_1.Messages.ErrorWithoutErrorCode(),
                toastType: widgets_1.ToastType.Error,
                testId: 'new-category-error',
            });
            onClose();
        },
        onSettled: () => queryClient.invalidateQueries({ queryKey }),
    });
    const { value: name, valid: nameValid, reset: resetName, element: NameWidget } = (0, catalog_core_1.useTextField)({
        initialState: '',
        label: catalog_messages_1.Messages.LabelCategoryName(),
        testId: exports.newCategoryTestId,
        max: characterLimit,
        style: { width: 200 },
        disabled: submit.isLoading,
        customTextValidation,
    });
    const { value: description, valid: descriptionValid, reset: resetDescription, element: DescriptionWidget } = (0, catalog_core_1.useTextFormatter)({
        initialState: '',
        label: catalog_messages_1.Messages.LabelCategoryDescription(),
        testId: exports.newCategoryTestId,
        max: 500,
        disabled: submit.isLoading,
        style: {},
    });
    const onClose = react_1.default.useCallback(() => {
        var _a;
        (_a = popoverInstance.current) === null || _a === void 0 ? void 0 : _a.toggle(false);
        resetName();
        resetDescription();
    }, [resetName, resetDescription, popoverInstance]);
    const submitDisabled = name.trim().length < 1 || nameValid !== undefined || description.length < 1
        || descriptionValid !== undefined || submit.isLoading;
    return (react_1.default.createElement("div", null,
        react_1.default.createElement(widgets_1.PopoverWidget, { "aria-describedby": 'popoverDescription', footerButtons: [
                { disabled: submit.isLoading, children: catalog_messages_1.Messages.Cancel(), onClick: onClose },
                {
                    disabled: submitDisabled,
                    showSpinner: submit.isLoading,
                    children: catalog_messages_1.Messages.Save(),
                    buttonType: widgets_1.ButtonType.Primary,
                    onClick: () => submit.mutate({ category: { name: name.trim(), description } }),
                },
            ], placement: 'right-end', popoverContent: react_1.default.createElement("form", null,
                NameWidget,
                DescriptionWidget), ref: popoverInstance, showTitleBar: true, title: catalog_messages_1.Messages.LabelAddNewCategory(), width: 400, triggerElement: react_1.default.createElement(widgets_1.ButtonWidget, null, catalog_messages_1.Messages.LabelNewCategory()) })));
}
exports.NewCategoryPopover = NewCategoryPopover;
function CategoryDescription(props) {
    const [customCategoryDescription, refetch] = useLabelCategoryDescription(props.category);
    const lowered = props.category.toLowerCase();
    if (lowered === 'warning' || lowered === 'sensitivity' || customCategoryDescription === '') {
        return react_1.default.createElement(react_1.default.Fragment, null);
    }
    return (react_1.default.createElement(react_1.default.Fragment, null,
        react_1.default.createElement("div", { style: { paddingBottom: 8 } },
            catalog_messages_1.Messages.LabelCategoryDescriptionHeading(),
            react_1.default.createElement("span", { style: { display: 'inline-block', whiteSpace: 'nowrap', marginLeft: 6 } },
                react_1.default.createElement(EditCategoryPopover, { categoryName: props.category, description: customCategoryDescription, onSuccess: ({ category }) => {
                        props.setCategory(category);
                        refetch();
                    } }))),
        react_1.default.createElement("div", { style: { display: 'block', overflow: 'hidden' } },
            react_1.default.createElement("span", { style: { display: 'inline' } },
                react_1.default.createElement(MarkupStyles_1.FormattedMarkupWrapperInTooltip, { markup: customCategoryDescription })))));
}
function CategoryHelpInformation(props) {
    const category = props.category.toLowerCase();
    const helpToken = getHelpToken(category);
    const learnMoreLink = '[CATEGORY_DESCRIPTION_TOKEN]';
    const helpUrl = (0, catalog_help_url_1.useServerHelpUrlByToken)({ helpToken });
    const link = {
        'CATEGORY_DESCRIPTION_TOKEN': (react_1.default.createElement(widgets_1.TextLinkWidget, { href: helpUrl, testId: exports.newCategoryTestId + '-helpUrl', target: '_blank', rel: 'noopener noreferrer' }, catalog_messages_1.Messages.LabelCategoryDescriptionLearnMoreLink())),
    };
    return (react_1.default.createElement("div", { style: { display: 'block' } },
        react_1.default.createElement("span", { style: { position: 'relative', bottom: -2 } },
            react_1.default.createElement(NotificationInfoOutlineIcon_1.NotificationInfoOutlineIcon, { size: 14, alt: catalog_messages_1.Messages.CustomLabelDialogTooltip() })),
        react_1.default.createElement("span", { style: { paddingLeft: 4 } },
            react_1.default.createElement(HelpString, { category: category, learnMoreLink: learnMoreLink, link: link }))));
}
function getHelpToken(category) {
    switch (category) {
        case 'warning':
            return 'label-dqw-downstream';
        case 'sensitivity':
            return 'label-sensitivity-downstream';
        default:
            return 'label-custom-downstream';
    }
}
function HelpString({ category, learnMoreLink, link }) {
    let baseString = catalog_messages_1.Messages.LabelCategoryCustomDescription({ learnMoreLink });
    if (category === 'warning') {
        baseString = catalog_messages_1.Messages.LabelCategoryWarningDescription({ learnMoreLink });
    }
    else if (category === 'sensitivity') {
        baseString = catalog_messages_1.Messages.LabelCategorySensitivityDescription({ learnMoreLink });
    }
    const interpolated = (0, catalog_core_1.interpolateString)(baseString, link);
    return react_1.default.createElement(react_1.default.Fragment, null, interpolated.map((t, i) => react_1.default.createElement(react_1.default.Fragment, { key: i.toString() }, t)));
}
function EditCategoryPopover({ categoryName, description: initialDescription, onSuccess: optionalOnSuccess }) {
    const queryClient = (0, react_query_1.useQueryClient)();
    const popoverInstance = react_1.default.useRef(null);
    const mutationFn = (0, catalog_core_1.useRestFn)(labelCategories_1.updateLabelCategory);
    const onClose = react_1.default.useCallback(() => {
        var _a;
        (_a = popoverInstance.current) === null || _a === void 0 ? void 0 : _a.toggle(false);
    }, [popoverInstance]);
    const onSuccess = react_1.default.useCallback((category, description) => {
        widgets_1.ToastWidget.show({
            message: catalog_messages_1.Messages.LabelCategoryUpdated({ Category: (0, LabelHelpers_1.localizeLabelCategory)(category) }),
            toastType: widgets_1.ToastType.Success,
            testId: 'edit-category-success',
        });
        onClose();
        if (optionalOnSuccess) {
            optionalOnSuccess({ category, description });
        }
    }, [onClose, optionalOnSuccess]);
    const submit = (0, react_query_1.useMutation)({
        mutationFn,
        onSuccess: (data) => onSuccess(data.name, (0, labelCategories_1.isCustomLabelCategory)(data) ? data.description : undefined),
        onError: () => {
            widgets_1.ToastWidget.show({
                message: catalog_messages_1.Messages.ErrorWithoutErrorCode(),
                toastType: widgets_1.ToastType.Error,
                testId: 'edit-category-error',
            });
            onClose();
        },
        onSettled: () => queryClient.invalidateQueries({ queryKey }),
    });
    const customTextValidation = (0, useCustomNameValidation_1.useCustomNameValidation)(queryKey, catalog_messages_1.Messages.LabelCategoryAlreadyExists(), labelCategories_1.getLabelCategories, categoryName);
    const [categoryDescription] = useLabelCategoryDescription(categoryName, initialDescription);
    const { value: name, valid: nameValid, element: NameWidget } = (0, catalog_core_1.useTextField)({
        initialState: categoryName,
        label: catalog_messages_1.Messages.LabelCategoryName(),
        testId: exports.newCategoryTestId,
        max: characterLimit,
        style: { width: 200 },
        disabled: submit.isLoading,
        customTextValidation: submit.isLoading ? undefined : customTextValidation,
    });
    const { value: description, valid: descriptionValid, element: DescriptionWidget } = (0, catalog_core_1.useTextFormatter)({
        initialState: categoryDescription ? categoryDescription : '',
        label: catalog_messages_1.Messages.LabelCategoryDescription(),
        testId: exports.newCategoryTestId,
        max: 500,
        disabled: submit.isLoading,
        style: {},
    });
    const submitDisabled = submit.isLoading || name.trim().length < 1 || nameValid !== undefined || description.length < 1
        || descriptionValid !== undefined;
    if ((0, LabelCategories_1.isBuiltInLabelCategory)(categoryName)) {
        return null;
    }
    return (react_1.default.createElement("div", null,
        react_1.default.createElement(widgets_1.PopoverWidget, { "aria-describedby": 'popoverDescription', footerButtons: [
                {
                    disabled: submit.isLoading,
                    children: catalog_messages_1.Messages.Cancel(),
                    onClick: onClose,
                },
                {
                    disabled: submitDisabled,
                    showSpinner: submit.isLoading,
                    children: catalog_messages_1.Messages.Save(),
                    buttonType: widgets_1.ButtonType.Primary,
                    onClick: () => submit.mutate({
                        category: { name: name.trim(), description: description.trim() },
                        existingCategoryName: categoryName,
                    }),
                },
            ], placement: 'right-end', popoverContent: react_1.default.createElement("form", null,
                NameWidget,
                DescriptionWidget), ref: popoverInstance, showTitleBar: true, title: catalog_messages_1.Messages.LabelEditCategory(), width: 400, triggerElement: react_1.default.createElement(widgets_1.ButtonWidget, { buttonType: widgets_1.ButtonType.Icon, getContent: buttonContent, testId: 'edit-label-category' }) })));
}
exports.EditCategoryPopover = EditCategoryPopover;
const buttonContent = () => {
    return (react_1.default.createElement("span", { className: 'editLabelCategory', style: {} },
        react_1.default.createElement(PencilBaseIcon_1.PencilBaseIcon, { size: 12, alt: 'Edit label category pencil icon' })));
};
// @deprecated - this should be private
function useLabelCategoryDescription(categoryName, description) {
    const queryFn = (0, catalog_core_1.useRestFnNoParams)(labelCategories_1.getLabelCategories);
    const { data, refetch } = (0, react_query_1.useQuery)(['category', categoryName], { queryFn, enabled: !description });
    const category = data === null || data === void 0 ? void 0 : data.find(t => t.name === categoryName);
    if (category && (0, labelCategories_1.isSystemLabelCategory)(category)) {
        return ['', refetch];
    }
    else if (category && (0, labelCategories_1.isCustomLabelCategory)(category)) {
        return [category.description, refetch];
    }
    else {
        return [description !== null && description !== void 0 ? description : '', refetch];
    }
    // const resultDescription: string = data?.find(t => t.name === categoryName)
    // return [data?.find(t => t.value === categoryName)?.tooltip ?? description ?? '', refetch]
}
exports.useLabelCategoryDescription = useLabelCategoryDescription;
function mapLabelCategoriesAsListItems(categories) {
    if (!categories) {
        return [];
    }
    const system = categories
        .filter(labelCategories_1.isSystemLabelCategory)
        .filter(t => `${t.name}`.toLowerCase() !== 'certification')
        .map(t => ({ value: t.name, content: (0, LabelHelpers_1.localizeLabelCategory)(t.name) }))
        .sort((a, b) => a.content.localeCompare(b.content));
    if (system.length > 0) {
        system.unshift({ value: catalog_messages_1.Messages.LabelBuiltInCategories(), group: true });
    }
    const custom = categories
        .filter(labelCategories_1.isCustomLabelCategory)
        .map(t => ({
        value: t.name,
        tooltip: t.description,
    }))
        .sort((a, b) => a.value.localeCompare(b.value));
    if (custom.length > 0) {
        system.push({ separator: true });
        custom.unshift({ value: catalog_messages_1.Messages.LabelCustomCategories(), group: true });
    }
    return [
        ...system,
        ...custom,
    ];
}
//# sourceMappingURL=NewCategoryExperience.js.map